import { atom, type Atom } from 'jotai';
import { withAtomEffect } from 'jotai-effect';
import { atomWithDefault } from 'jotai/utils';

type LeverageUpdater = number | ((prev: number) => number);

/**
 * 입력 leverage를 항상 min(input, maxLeverage)로 clamp하는 atom을 만든다.
 * - 읽기: min(raw, max) — max가 내려가면 즉시 반영(슬라이더 밖 값이 남지 않음).
 * - **sticky**: max가 raw보다 낮아지면 raw 자체를 낮춰 저장한다. 이후 max가 다시 올라도
 *   값은 복구되지 않는다 (예: 150→max50이면 50, 그 뒤 max150이 돼도 50 유지).
 * - 쓰기(슬라이더/버튼/직접입력)도 현재 max로 clamp.
 *
 * jotai/jotai-effect 외 의존이 없어(dep-free) bunja 스코프나 socket 그래프 없이
 * plain jotai store로 단독 unit test된다(clamped-leverage-atom.test.ts).
 * sticky 동작은 atom이 mount(구독)된 동안에만 적용된다(모달 렌더 중 = 항상 mount됨).
 */
export const createClampedLeverageAtom = (defaultLeverage: number, maxLeverageAtom: Atom<number>) => {
  const rawLeverageAtom = atomWithDefault<number>(() => defaultLeverage);

  const clampedAtom = atom(
    (get) => Math.min(get(rawLeverageAtom), get(maxLeverageAtom)),
    (get, set, next: LeverageUpdater) => {
      const max = get(maxLeverageAtom);
      const prev = Math.min(get(rawLeverageAtom), max);
      const value = typeof next === 'function' ? next(prev) : next;
      set(rawLeverageAtom, Math.min(value, max));
    },
  );

  return withAtomEffect(clampedAtom, (get, set) => {
    const max = get(maxLeverageAtom);
    // max가 raw보다 낮아지면 raw를 실제로 낮춰 sticky하게 만든다(복구 방지).
    if (get.peek(rawLeverageAtom) > max) set(rawLeverageAtom, max);
  });
};
